// client/src/components/stats/cbetMatchupBenchmarks.ts
//
// Benchmarks de CBet Flop por matchup posicional (hero × villain).
// Source: theoretical — derivados de consensus de coaching content
// público y outputs de solver para situaciones canónicas.
//
// IMPORTANTE: estos rangos son APROXIMADOS y dependen fuertemente de:
// - SPR del flop (cbet óptimo cambia mucho entre SPR 3 y SPR 8)
// - Textura del board (dry vs wet)
// - Tendencias del villain pool
//
// Cuando agreguemos Fase 4 (texture filter) y Population Benchmarks,
// estos rangos se van a refinar significativamente.

import { MatchupKey } from '../../utils/pokerStats/types';
import { Benchmark } from './benchmarks';
import { StackKey } from './leakRanges';

// ─────────────────────────────────────────────────────────────────────────────
// Benchmarks por matchup. Solo incluimos los matchups donde hay consenso
// claro de solver/coaching. Matchups raros (EP_vs_HJ, etc.) NO tienen
// benchmark — la UI los va a mostrar sin evaluación.
//
// Lectura general:
// - Hero como opener temprano (EP/HJ) vs blinds: cbet ALTO (rango polarizado)
// - Hero late position (CO/BTN) vs blinds: cbet MEDIO (rango más merge)
// - Hero vs late position (vs CO/BTN): cbet BAJO (villain tiene rango fuerte)
// - SB vs BB: cbet MEDIO-BAJO (BB ya defendió wide, no fold mucho)
// - Multiway: cbet BAJO (más equity en contra)
// ─────────────────────────────────────────────────────────────────────────────

const THEORETICAL_CBET_MATCHUP_BENCHMARKS_ALL: Partial<Record<MatchupKey, Benchmark>> = {
  // Hero EP vs * — opens tightos, value-heavy ranges
  'EP_vs_BB':  { min: 60, max: 80, source: 'theoretical' },
  'EP_vs_SB':  { min: 60, max: 78, source: 'theoretical' },
  'EP_vs_BTN': { min: 40, max: 60, source: 'theoretical' },
  'EP_vs_CO':  { min: 45, max: 65, source: 'theoretical' },
  'EP_vs_HJ':  { min: 50, max: 70, source: 'theoretical' },

  // Hero HJ vs *
  'HJ_vs_BB':  { min: 65, max: 82, source: 'theoretical' },
  'HJ_vs_SB':  { min: 60, max: 78, source: 'theoretical' },
  'HJ_vs_BTN': { min: 35, max: 55, source: 'theoretical' },
  'HJ_vs_CO':  { min: 40, max: 60, source: 'theoretical' },

  // Hero CO vs *
  'CO_vs_BB':  { min: 55, max: 72, source: 'theoretical' },
  'CO_vs_SB':  { min: 55, max: 72, source: 'theoretical' },
  'CO_vs_BTN': { min: 32, max: 50, source: 'theoretical' },

  // Hero BTN vs *
  'BTN_vs_BB': { min: 50, max: 68, source: 'theoretical' },
  'BTN_vs_SB': { min: 50, max: 68, source: 'theoretical' },

  // Hero SB vs BB — situación HU específica
  'SB_vs_BB':  { min: 45, max: 62, source: 'theoretical' },

  // Multiway — más selectivo en general
  'multi':     { min: 30, max: 50, source: 'theoretical' },
};

// Por ahora un solo set para todos los stack levels.
// El refinement por stack lo agregamos cuando tengamos sample suficiente
// en buckets específicos para validar diferencias reales.
export const CBET_MATCHUP_BENCHMARKS: Record<StackKey, Partial<Record<MatchupKey, Benchmark>>> = {
  short: THEORETICAL_CBET_MATCHUP_BENCHMARKS_ALL,
  mid:   THEORETICAL_CBET_MATCHUP_BENCHMARKS_ALL,
  deep:  THEORETICAL_CBET_MATCHUP_BENCHMARKS_ALL,
  all:   THEORETICAL_CBET_MATCHUP_BENCHMARKS_ALL,
};

export function getCbetMatchupBenchmark(
  matchup: MatchupKey,
  stack: StackKey
): Benchmark | null {
  const stackBenchmarks = CBET_MATCHUP_BENCHMARKS[stack] || CBET_MATCHUP_BENCHMARKS.all;
  return stackBenchmarks[matchup] || null;
}
